// Изолинии механизмов апвеллинга: уровни, геометрия, подписи.
//
// СЧИТАЕТ КЛИЕНТ, а не сервер — в отличие от изобат. Три довода:
//
// 1. Уровни зависят от КАДРА, а не от даты. Растр меряет абсолютно
//    (фиксированная шкала ±6 м/сут), линии — относительно того, что в кадре:
//    вихревая накачка с размахом ±0,5 м/сут на общей шкале бледная, а по своему
//    диапазону читается. Серверный контурер пришлось бы кэшировать по пяти
//    измерениям (механизм, дата, глубина, bbox, зум) с промахом почти на
//    каждый пан.
// 2. Переключение «заливка ↔ линии» обязано быть мгновенным: один привезённый
//    массив кормит и контуры, и края шкалы легенды, и подпись шага.
// 3. Исходник — 1/12°, то есть ~10–20 тыс. ячеек на вьюпорт, ровно как сетка
//    SST. Серверный путь оправдан там, где геометрия статична, а исходник
//    гигантский (изобаты: 2,88 млн точек GEBCO на кадр).

import { contourAtLevel } from '../maps/marchingSquares'
import type { SstGrid } from '../maps/marchingSquares'
import { formatNumber } from '../i18n'
import type { Lang } from '../i18n/types'

/** Сколько линий рисуем на механизм. Больше — штриховка вместо карты. */
export const ISO_MAX_LINES = 9

/** Нулевая линия — ОСОБЫЙ уровень: единственный, чей смысл не зависит от
 *  величины. Граница двух бледных тонов растра не локализуется глазом до
 *  пикселя, а весь вопрос раздела — «где эта граница». */
export const ZERO_LEVEL = 0

function round3(v: number): number {
  return Math.round(v * 1000) / 1000
}

/**
 * Уровни по ДИАПАЗОНУ КАДРА, а не по общей шкале слоя.
 *
 * Для знакового поля набор СИММЕТРИЧЕН относительно нуля: подъём и опускание —
 * одно явление с разным знаком, и разная густота линий по сторонам читалась бы
 * как разная изменчивость. Ноль добавляется отдельно (`zero`), потому что
 * подписи ему не нужны и выключается он своим тумблером.
 */
export function isoLevels(
  vmin: number | null | undefined,
  vmax: number | null | undefined,
  opts: { signed: boolean; zero: boolean; max?: number },
): number[] {
  if (vmin == null || vmax == null || !Number.isFinite(vmin) || !Number.isFinite(vmax)) return []
  if (vmax <= vmin) return opts.zero && opts.signed && vmin <= 0 && vmax >= 0 ? [ZERO_LEVEL] : []

  const max = opts.max ?? ISO_MAX_LINES
  const out: number[] = []

  if (opts.signed) {
    const span = Math.max(Math.abs(vmin), Math.abs(vmax))
    if (!(span > 0)) return opts.zero ? [ZERO_LEVEL] : []
    // Половина потолка на каждую сторону; ноль идёт отдельно.
    const perSide = Math.max(1, Math.floor((max - (opts.zero ? 1 : 0)) / 2))
    const step = span / (perSide + 1)
    for (let k = perSide; k >= 1; k--) out.push(round3(-k * step))
    if (opts.zero) out.push(ZERO_LEVEL)
    for (let k = 1; k <= perSide; k++) out.push(round3(k * step))
    return out.filter(v => v >= vmin - 1e-9 && v <= vmax + 1e-9)
  }

  const step = (vmax - vmin) / (max + 1)
  for (let k = 1; k <= max; k++) out.push(round3(vmin + k * step))
  return out
}

export type UpwIsoProps = { level: number; zero: boolean; color: string; mechanism: string }
export type UpwIsoLabelProps = { level: number; color: string; mechanism: string }

export interface UpwIsoFamily {
  contours: GeoJSON.FeatureCollection<GeoJSON.MultiLineString, UpwIsoProps>
  labels: GeoJSON.FeatureCollection<GeoJSON.Point, UpwIsoLabelProps>
}

/**
 * Линии и подписи одного механизма.
 *
 * Геометрия и подписи считаются ОДНИМ проходом на уровень: соседний инструмент
 * (`waveContours`) зовёт `contourAtLevel` дважды, но там уровней шесть, а здесь
 * их до девяти на КАЖДЫЙ включённый механизм.
 *
 * `zero` помечает нулевую фичу — по ней слой рисует её отдельным начертанием
 * (толще и без подписи): это не «ещё одна линия», а граница явления.
 */
export function buildUpwIso(
  grid: SstGrid,
  levels: number[],
  color: string,
  mechanism: string,
  opts?: { labels?: boolean },
): UpwIsoFamily {
  const withLabels = opts?.labels ?? true
  const sepLat = Math.max((grid.stepLat ?? grid.step) * 8, 0.4)
  const sepLon = Math.max((grid.stepLon ?? grid.step) * 8, 0.4)

  const contours: GeoJSON.Feature<GeoJSON.MultiLineString, UpwIsoProps>[] = []
  const labels: GeoJSON.Feature<GeoJSON.Point, UpwIsoLabelProps>[] = []

  for (const level of levels) {
    const f = contourAtLevel(grid, level)
    const zero = level === ZERO_LEVEL
    contours.push({ ...f, properties: { level, zero, color, mechanism } })
    // У нулевой линии подписи нет: её значение и так сказано в легенде, а
    // ярлык «0» на границе двух знаков только загораживает саму границу.
    if (!withLabels || zero) continue
    const seen = new Set<string>()
    for (const [a, b] of f.geometry.coordinates) {
      const lon = (a[0] + b[0]) / 2
      const lat = (a[1] + b[1]) / 2
      const bucket = `${Math.round(lon / sepLon)},${Math.round(lat / sepLat)}`
      if (seen.has(bucket)) continue
      seen.add(bucket)
      labels.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lon, lat] },
        properties: { level, color, mechanism },
      })
    }
  }

  return {
    contours: { type: 'FeatureCollection', features: contours },
    labels: { type: 'FeatureCollection', features: labels },
  }
}

/** Текст ярлыка: величина со знаком. Знак обязателен — линия «−2» и линия «2»
 *  описывают противоположные явления, и без знака их не различить. */
export function upwLabelText(level: number, lang: Lang): string {
  // Без явного числа знаков: хвостовые нули не дописываются — подпись читают
  // на карте, а не в таблице (то же правило, что у ярлыка изотермы).
  const s = formatNumber(lang, Math.abs(level))
  return level < 0 ? `−${s}` : level > 0 ? `+${s}` : '0'
}
